import type { RxJsonSchema } from 'rxdb'

/**
 * Status Document Type (Denormalized for RxDB)
 */
export interface StatusDocument {
  id: string
  envId: string
  /**
   * Projects this status is scoped to. Empty array = global (visible to all projects
   * in the workspace). A non-empty array means the status is only visible to those
   * specific projects. System-generated statuses are always global and cannot have
   * entries here.
   */
  projectIds: Array<string>
  name: string
  progressType: string
  description: string
  sortOrder: string
  isSystemGenerated: boolean
  isDefault: boolean
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Status
 */
export const statusSchema: RxJsonSchema<StatusDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    projectIds: {
      type: 'array',
      default: [],
      items: {
        type: 'string',
        maxLength: 36,
      },
    },
    name: {
      type: 'string',
      maxLength: 50,
    },
    progressType: {
      type: 'string',
      maxLength: 20,
    },
    description: {
      type: 'string',
      maxLength: 200,
    },
    sortOrder: {
      type: 'string',
      maxLength: 50,
    },
    isSystemGenerated: {
      type: 'boolean',
      default: false,
    },
    isDefault: {
      type: 'boolean',
      default: false,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'name',
    'progressType',
    'description',
    'sortOrder',
    'isSystemGenerated',
    'isDefault',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    ['envId', 'name'],
    'updatedOn',
    ['envId', 'isDeleted'],
  ],
}

export const statusMigrationStrategies = {
  // v0 -> v1: replaced singular `projectId` with array `projectIds`. Convert
  // the old single id into a single-element array when present.
  1: (oldDoc: any): any => {
    const { projectId, ...rest } = oldDoc
    return {
      ...rest,
      projectIds: Array.isArray(oldDoc.projectIds)
        ? oldDoc.projectIds
        : (projectId ? [projectId] : []),
    }
  },
}
